import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import PropTypes from 'prop-types';
import { timestampFormatter } from '../../../transforms';

const STATUS_MAP = {
  PRE_TRANSIT: {
    name: 'Pre Transit',
    description:
      'The label is created but before the package is dropped off or picked up by the carrier.',
    icon: ClockIcon,
    iconColor: 'text-gray-500',
  },
  TRANSIT: {
    name: 'In Transit',
    description:
      'The package has been scanned by the carrier and is in transit.',
    icon: TruckIcon,
    iconColor: 'text-callouts-info',
  },
  DELIVERED: {
    name: 'Delivered',
    description: 'The package has been successfully delivered.',
    icon: CheckCircleIcon,
    iconColor: 'text-green-500',
  },
  RETURNED: {
    name: 'Returned',
    description:
      'The package is en route to be returned to the sender, or has been returned successfully.',
    icon: ArrowUturnLeftIcon,
    iconColor: 'text-callouts-warning',
  },
  FAILURE: {
    name: 'Failed',
    description:
      'The carrier indicated that there has been an issue with the delivery. This can happen for various reasons and depends on the carrier. This status does not indicate a technical, but a delivery issue.',
    icon: XCircleIcon,
    iconColor: 'text-callouts-danger',
  },
  UNKNOWN: {
    name: 'Unknown',
    description:
      "The package has not been found via the carrier's tracking system.",
    icon: QuestionMarkCircleIcon,
    iconColor: 'text-callouts-warning',
  },
};

function ShippingHistoryFeed({ events }) {
  function statusMapper(status) {
    const statusName = typeof status === 'string' ? status : status.status;

    return (
      STATUS_MAP[statusName] || {
        name: status.status,
        description: null,
        icon: QuestionMarkCircleIcon,
        iconColor: 'text-callouts-warning',
      }
    );
  }

  return (
    <ul className="mt-4 space-y-6">
      {events.map((event, eventIdx) => {
        const StatusIcon = statusMapper(event.trackingStatus).icon;

        return (
          <li key={event.id} className="relative flex gap-x-4">
            <div
              className={`${eventIdx === events.length - 1 ? 'h-6' : '-bottom-6'} absolute top-0 left-0 flex w-6 justify-center`}
            >
              <div className="w-px bg-gray-200" />
            </div>
            {event.event === 'track_updated' && (
              <>
                <StatusIcon
                  aria-hidden="true"
                  className={`relative mt-3 size-6 flex-none rounded-full bg-gray-50 ${statusMapper(event.trackingStatus).iconColor}`}
                />

                <div className="flex-auto rounded-md p-3 ring-1 ring-gray-300 ring-inset">
                  <div className="flex justify-between gap-x-4">
                    <div className="py-0.5 text-gray-500 text-xs/5">
                      <span className="font-medium text-gray-900">
                        Tracking Updated
                      </span>
                      &nbsp;
                      <span className="pointer hover:text-rcprimary-400">
                        {statusMapper(event.trackingStatus).name}
                      </span>
                    </div>
                    <div className="flex-none py-0.5 text-gray-500 text-xs/5">
                      {timestampFormatter(event.createdAt)}
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm/4">
                    <span className="text-medium">
                      {event.trackingStatus?.substatus?.text}
                    </span>
                    <br />
                    <br />
                    <span className="text-gray-500 text-xs italic">
                      {statusMapper(event.trackingStatus).description}
                    </span>
                    <br />
                    {event.trackingStatus?.location &&
                      statusMapper(event.trackingStatus).name !== 'Delivered' &&
                      Object.keys(event.trackingStatus?.location).length >
                        0 && (
                        <>
                          <span className="text-gray-500 text-xs">
                            Current Location:&nbsp;
                            {event.trackingStatus?.location?.city},&nbsp;
                            {event.trackingStatus?.location?.state}
                            &nbsp;
                            {event.trackingStatus?.location?.zip}
                          </span>
                          <br />
                        </>
                      )}
                    {event.addressTo &&
                      Object.keys(event.addressTo).length > 0 && (
                        <span className="text-gray-500 text-xs">
                          {statusMapper(event.trackingStatus).name ===
                          'Delivered'
                            ? 'Arrived At:'
                            : 'En Route To:'}
                          &nbsp;
                          {event.addressTo?.city}, {event.addressTo?.state}
                          &nbsp;
                          {event.addressTo?.zip}
                        </span>
                      )}
                  </p>
                </div>
              </>
            )}

            {event.event.includes('transaction') && (
              <>
                <div className="relative flex size-6 flex-none items-center justify-center bg-white">
                  {event.event === 'transaction_created' ? (
                    <CheckCircleIcon
                      aria-hidden="true"
                      className="size-6 text-rcprimary-400"
                    />
                  ) : (
                    <div className="size-1.5 rounded-full bg-gray-100 ring-1 ring-gray-300" />
                  )}
                </div>
                <p className="flex-auto py-0.5 text-gray-500 text-xs/5">
                  <span className="font-medium text-gray-900">
                    Label&nbsp;
                    {event.event === 'transaction_created'
                      ? 'Created'
                      : 'Updated'}
                  </span>
                  &nbsp;
                  <span className="pointer hover:text-rcprimary-400">
                    {statusMapper(event.trackingStatus).name}
                  </span>
                </p>
                <div className="flex-none py-0.5 text-gray-500 text-xs/5">
                  {timestampFormatter(event.createdAt)}
                </div>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

ShippingHistoryFeed.propTypes = {
  events: PropTypes.array.isRequired,
};

export default ShippingHistoryFeed;
